import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import EcoPagination from "../../../components/eco-ui/EcoPagination";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import { useGoBack } from "../../../hooks/useGoBack";
import type { RootState } from "../../../store/store";
import { getStoreScheduleSummaryApi } from "../api/scheduleApi";
import { DpCostCenter, listCostCentersApi } from "../api/settingsApi";
import {
  closeManyTimesheetsApi,
  describeIssue,
  DpTimesheetList,
  DpTimesheetRow,
  duration,
  exportTimesheetsApi,
  getTimesheetsSummaryApi,
  listTimesheetsApi,
  recomputeMonthApi,
  SIGNATURE_BADGE,
  SIGNATURE_LABELS,
  SignatureFilter,
  TimesheetListParams,
} from "../api/timekeepingApi";
import { DP_ADJUSTMENTS_PAGE, DP_FLAGGED_PUNCHES_PAGE, DP_PAGE, DP_PRESENCE_PAGE, DP_TIMESHEET_PAGE } from "../routes";
import { downloadBlob, formatCpf, MONTH_LABELS } from "../utils/format";
import "./Dp.css";
import "./DpEco.css";
import "./Timekeeping.css";

const PAGE_SIZE = 20;
const STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "ABERTO", label: "Abertos" },
  { value: "FECHADO", label: "Fechados" },
  { value: "BLOQUEADO", label: "Com bloqueio" },
  { value: "COM_PENDENCIA", label: "Com pedido pendente" },
];

const SIGNATURE_OPTIONS: Array<{ value: "" | SignatureFilter; label: string }> = [
  { value: "", label: "Todas" },
  { value: "PENDENTE", label: "Pendente" },
  { value: "ASSINADO", label: "Assinado" },
  { value: "CONTESTADO", label: "Contestado" },
  { value: "ATRASADO", label: "Atrasado (além do prazo)" },
];

/** Badge da assinatura: só faz sentido depois do fechamento (ou quando a reabertura invalidou). */
const SignatureBadge = ({ r }: { r: DpTimesheetRow }) => {
  const sig = r.signature;
  if (!sig || sig.status === "NAO_FECHADO") return <span className="dpMuted">—</span>;
  return (
    <span className={`dpBadge ${SIGNATURE_BADGE[sig.status]}`} title={sig.lastContest?.text}>
      {SIGNATURE_LABELS[sig.status]}
      {sig.overdue ? ` · ${sig.daysPending} dias` : ""}
    </span>
  );
};

const nameOf = (r: DpTimesheetRow) => r.employee.socialName || r.employee.fullName;
const pct = (n: number) => `${Math.round(n * 100)}%`;

type CloseResult = { closed: number; blocked: Array<{ contractId: string; employeeName: string; message: string; blockers: string[] }> };

/**
 * Ponto do mês por loja: uma linha por colaborador, com o previsto, o
 * trabalhado, horas extras, faltas, atrasos, pedidos de ajuste e o que impede o
 * fechamento. Daqui o DP abre o espelho e fecha o mês, um a um ou a loja inteira.
 */
export default function TimesheetsPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const [params, setParams] = useSearchParams();

  const now = new Date();
  const year = Number(params.get("ano")) || now.getFullYear();
  const month = Number(params.get("mes")) || now.getMonth() + 1;
  const storeId = params.get("loja") ?? "";
  const costCenterId = params.get("cc") ?? "";
  const status = params.get("situacao") ?? "";
  const signature = (params.get("assinatura") ?? "") as "" | SignatureFilter;
  const [q, setQ] = useState(params.get("busca") ?? "");
  const [page, setPage] = useState(1);

  const [stores, setStores] = useState<Array<{ storeId: string; storeName: string }>>([]);
  const [costCenters, setCostCenters] = useState<DpCostCenter[]>([]);
  const [data, setData] = useState<DpTimesheetList | null>(null);
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<null | "selected" | "store">(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CloseResult | null>(null);

  const setFilter = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
    setPage(1);
    setSelected(new Set());
  };

  useEffect(() => {
    if (!token) return;
    getStoreScheduleSummaryApi(token)
      .then((s) => setStores(s.stores.map(({ storeId: id, storeName }) => ({ storeId: id, storeName }))))
      .catch(() => setStores([]));
    listCostCentersApi(token)
      .then(setCostCenters)
      .catch(() => setCostCenters([]));
    getTimesheetsSummaryApi(token)
      .then((s) => setEnabled(s.enabled))
      .catch(() => setEnabled(null));
  }, [token]);

  const filters = useMemo<TimesheetListParams>(
    () => ({
      year,
      month,
      storeId: storeId && storeId !== "SEM_LOJA" ? storeId : undefined,
      withoutStore: storeId === "SEM_LOJA",
      costCenterId: costCenterId || undefined,
      status: status || undefined,
      signature: signature || undefined,
      q: params.get("busca") || undefined,
    }),
    [year, month, storeId, costCenterId, status, signature, params],
  );

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      setData(await listTimesheetsApi(token, { ...filters, page, limit: PAGE_SIZE }));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar o ponto do mês.");
    } finally {
      setLoading(false);
    }
  }, [token, filters, page]);

  const exportCsv = async () => {
    if (!token) return;
    setBusy(true);
    try {
      const blob = await exportTimesheetsApi(token, filters);
      const store = stores.find((s) => s.storeId === storeId)?.storeName;
      downloadBlob(blob, `ponto-${year}-${String(month).padStart(2, "0")}${store ? `-${store}` : ""}.csv`);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao exportar o CSV.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  // Busca dispara sozinha depois de uma pausa (mesmo padrão da lista de colaboradores).
  useEffect(() => {
    const id = setTimeout(() => {
      if ((params.get("busca") ?? "") !== q.trim()) setFilter({ busca: q.trim() });
    }, 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const shiftMonth = (delta: number) => {
    const d = new Date(year, month - 1 + delta, 1);
    setFilter({ ano: String(d.getFullYear()), mes: String(d.getMonth() + 1) });
  };

  const toggle = (contractId: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(contractId)) next.delete(contractId);
      else next.add(contractId);
      return next;
    });

  const openRows = useMemo(() => (data?.items ?? []).filter((r) => r.status === "ABERTO"), [data]);
  const allOpenSelected = openRows.length > 0 && openRows.every((r) => selected.has(r.contractId));

  const closeMany = async () => {
    if (!token || !confirm) return;
    setBusy(true);
    try {
      const res = await closeManyTimesheetsApi(token, {
        year,
        month,
        ...(confirm === "store" ? { storeId: storeId || undefined } : { contractIds: Array.from(selected) }),
      });
      setConfirm(null);
      setSelected(new Set());
      setResult(res);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao fechar os espelhos.");
    } finally {
      setBusy(false);
    }
  };

  const recompute = async () => {
    if (!token) return;
    setBusy(true);
    try {
      const res = await recomputeMonthApi(token, year, month, storeId && storeId !== "SEM_LOJA" ? storeId : undefined);
      toast.success(`${res.recomputed} espelho(s) recalculado(s).`);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao recalcular.");
    } finally {
      setBusy(false);
    }
  };

  if (!token) return null;
  const t = data?.totals;
  const storeName = stores.find((s) => s.storeId === storeId)?.storeName;
  const monthEnded = new Date(year, month, 1) <= new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const issues = (r: DpTimesheetRow) => [...r.blockers, ...r.warnings];
  const open = (r: DpTimesheetRow) => navigate(DP_TIMESHEET_PAGE(r.contractId, year, month));

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          <EcoPageHeader
            crumbs={[{ label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) }, { label: "Ponto" }]}
            title="Ponto e jornada"
            subtitle="Espelho de ponto do mês por loja: marcações, horas, faltas, ajustes e fechamento."
            onBack={goBack}
            backLabel="Voltar ao DP"
            actions={
              <>
                <button type="button" className="ecoBtn" onClick={() => navigate(DP_ADJUSTMENTS_PAGE())}>
                  Fila de ajustes{t?.pendingAdjustments ? ` (${t.pendingAdjustments})` : ""}
                </button>
                <button
                  type="button"
                  className="ecoBtn"
                  onClick={() => navigate(`${DP_PRESENCE_PAGE()}${storeId && storeId !== "SEM_LOJA" ? `?loja=${storeId}` : ""}`)}
                >
                  Presença de hoje
                </button>
                <button type="button" className="ecoBtn" onClick={() => navigate(DP_FLAGGED_PUNCHES_PAGE())}>
                  Marcações para conferir
                </button>
                <button type="button" className="ecoBtn" onClick={exportCsv} disabled={busy || !data?.total}>
                  Exportar CSV
                </button>
                <button type="button" className="ecoBtn" onClick={recompute} disabled={busy}>
                  Recalcular
                </button>
              </>
            }
          />

          {enabled === false ? (
            <p className="dpNotice dpNotice--warning">
              O ponto eletrônico ainda não foi ligado. Defina a data de início em Administração do sistema → Configurações → Ponto.
            </p>
          ) : null}

          <div className="dpControls">
            <div className="dpFormGroup" style={{ margin: 0, flex: "0 1 260px" }}>
              <label>Mês</label>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => shiftMonth(-1)} aria-label="Mês anterior">
                  ‹
                </button>
                <strong style={{ minWidth: 130, textAlign: "center" }}>
                  {MONTH_LABELS[month - 1]} de {year}
                </strong>
                <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => shiftMonth(1)} aria-label="Próximo mês">
                  ›
                </button>
              </div>
            </div>
            <div className="dpFormGroup" style={{ margin: 0 }}>
              <label htmlFor="tkStore">Loja</label>
              <select id="tkStore" className="dpSelect" value={storeId} onChange={(e) => setFilter({ loja: e.target.value })}>
                <option value="">Todas as lojas</option>
                {stores.map((s) => (
                  <option key={s.storeId} value={s.storeId}>
                    {s.storeName}
                  </option>
                ))}
                <option value="SEM_LOJA">Sem loja</option>
              </select>
            </div>
            <div className="dpFormGroup" style={{ margin: 0 }}>
              <label htmlFor="tkCostCenter">Centro de custo</label>
              <select id="tkCostCenter" className="dpSelect" value={costCenterId} onChange={(e) => setFilter({ cc: e.target.value })}>
                <option value="">Todos</option>
                {costCenters.map((cc) => (
                  <option key={cc.id} value={cc.id}>
                    {cc.code} — {cc.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="dpFormGroup" style={{ margin: 0 }}>
              <label htmlFor="tkStatus">Situação</label>
              <select id="tkStatus" className="dpSelect" value={status} onChange={(e) => setFilter({ situacao: e.target.value })}>
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="dpFormGroup" style={{ margin: 0 }}>
              <label htmlFor="tkSignature">Assinatura</label>
              <select id="tkSignature" className="dpSelect" value={signature} onChange={(e) => setFilter({ assinatura: e.target.value })}>
                {SIGNATURE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="dpFormGroup" style={{ margin: 0 }}>
              <label htmlFor="tkSearch">Colaborador</label>
              <input id="tkSearch" className="dpInput" placeholder="Nome ou CPF" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
          </div>

          {t ? (
            <div className="dpTkTotals">
              <div className="dpTkTotal">
                <span>Colaboradores</span>
                <strong>{t.employees}</strong>
              </div>
              <div className="dpTkTotal">
                <span>Fechados</span>
                <strong>
                  {t.closed}/{t.employees}
                </strong>
              </div>
              <div className={`dpTkTotal ${t.blocked ? "dpTkTotal--warn" : ""}`}>
                <span>Com bloqueio</span>
                <strong>{t.blocked}</strong>
              </div>
              <div
                className={`dpTkTotal ${t.signatureOverdue ? "dpTkTotal--warn" : ""}`}
                title="Assinados pelo colaborador / fechados. Atrasados = sem assinatura além do prazo (só informativo)."
              >
                <span>Assinados</span>
                <strong>
                  {t.signed}/{t.closed}
                  {t.signatureOverdue ? <small> · {t.signatureOverdue} atrasado(s)</small> : null}
                </strong>
              </div>
              <div className={`dpTkTotal ${t.contested ? "dpTkTotal--bad" : ""}`}>
                <span>Contestados</span>
                <strong>{t.contested}</strong>
              </div>
              <div className="dpTkTotal">
                <span>Trabalhado / previsto</span>
                <strong>
                  {duration(t.workedMinutes)} / {duration(t.expectedMinutes)}
                </strong>
              </div>
              <div className="dpTkTotal">
                <span>Horas extras</span>
                <strong>{duration(t.overtimeWorkdayMinutes + t.overtimeRestDayMinutes)}</strong>
              </div>
              <div className={`dpTkTotal ${t.absenceDays ? "dpTkTotal--bad" : ""}`}>
                <span>Faltas</span>
                <strong>{t.absenceDays}</strong>
              </div>
              <div className="dpTkTotal">
                <span>Atrasos e saídas antes</span>
                <strong>{duration(t.lateMinutes + t.earlyLeaveMinutes)}</strong>
              </div>
              <div className={`dpTkTotal ${t.adjustedRatio > 0.15 ? "dpTkTotal--warn" : ""}`} title="Parte das marcações que veio de ajuste. Alta = problema de celular, rede ou acesso.">
                <span>Marcações por ajuste</span>
                <strong>{pct(t.adjustedRatio)}</strong>
              </div>
            </div>
          ) : null}

          <div className="dpControls">
            <button
              type="button"
              className="dpBtn dpBtn--primary dpBtn--small"
              disabled={!selected.size || busy || !monthEnded}
              title={!monthEnded ? "O espelho só fecha depois que o mês termina." : undefined}
              onClick={() => setConfirm("selected")}
            >
              Fechar selecionados ({selected.size})
            </button>
            <button
              type="button"
              className="dpBtn dpBtn--secondary dpBtn--small"
              disabled={!storeId || storeId === "SEM_LOJA" || busy || !monthEnded}
              title={!storeId ? "Escolha uma loja." : !monthEnded ? "O espelho só fecha depois que o mês termina." : undefined}
              onClick={() => setConfirm("store")}
            >
              Fechar todos da loja
            </button>
            {loading ? <span className="dpMuted">Carregando...</span> : null}
          </div>

          <div className="dpTableWrapper dpTkTableDesktop">
            <table className="dpTable">
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox"
                      aria-label="Selecionar os abertos da página"
                      checked={allOpenSelected}
                      onChange={() => setSelected(allOpenSelected ? new Set() : new Set(openRows.map((r) => r.contractId)))}
                    />
                  </th>
                  <th style={{ minWidth: 220 }}>Colaborador</th>
                  <th>Trabalhado / previsto</th>
                  <th>HE útil / descanso</th>
                  <th>Noturno</th>
                  <th>Atrasos</th>
                  <th>Faltas (DSR)</th>
                  <th>Ajustes</th>
                  <th>Pendências e avisos</th>
                  <th>Situação</th>
                  <th>Assinatura</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {!data?.items.length ? (
                  <tr>
                    <td colSpan={12} className="dpEmpty">
                      {loading ? "Carregando..." : "Nenhum colaborador com ponto neste mês."}
                    </td>
                  </tr>
                ) : (
                  data.items.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Selecionar ${nameOf(r)}`}
                          disabled={r.status !== "ABERTO"}
                          checked={selected.has(r.contractId)}
                          onChange={() => toggle(r.contractId)}
                        />
                      </td>
                      <td>
                        <span className="dpTkPerson">
                          <strong>{nameOf(r)}</strong>
                          <small>
                            {r.contract.registrationNumber ? `Mat. ${r.contract.registrationNumber} · ` : ""}
                            {r.store?.nomeFantasia ?? "Sem loja"}
                          </small>
                        </span>
                      </td>
                      <td className="dpTkNum">
                        {duration(r.workedMinutes)} / {duration(r.expectedMinutes)}
                      </td>
                      <td className="dpTkNum">
                        {duration(r.overtimeWorkdayMinutes)} / {duration(r.overtimeRestDayMinutes)}
                      </td>
                      <td className="dpTkNum">{duration(r.nightComputedMinutes)}</td>
                      <td className="dpTkNum">{duration(r.lateMinutes + r.earlyLeaveMinutes)}</td>
                      <td className="dpTkNum">
                        {r.absenceDays}
                        {r.dsrLostCount ? ` (${r.dsrLostCount})` : ""}
                      </td>
                      <td className="dpTkNum">
                        {r.pendingAdjustments ? <span className="dpBadge dpBadge--orange">{r.pendingAdjustments} pendente(s)</span> : null}
                        {r.adjustedPunchCount ? <div className="dpMuted">{r.adjustedPunchCount} por ajuste</div> : r.pendingAdjustments ? null : "—"}
                      </td>
                      <td>
                        <div className="dpTkIssues">
                          {issues(r).length
                            ? issues(r).slice(0, 3).map((code) => (
                                <span key={code} className={`dpTkFlag ${r.blockers.includes(code) ? "" : "dpTkFlag--info"}`}>
                                  {describeIssue(code)}
                                </span>
                              ))
                            : "—"}
                          {issues(r).length > 3 ? <span className="dpMuted">+{issues(r).length - 3}</span> : null}
                        </div>
                      </td>
                      <td>
                        <span className={`dpBadge ${r.status === "FECHADO" ? "dpBadge--green" : r.blockers.length ? "dpBadge--orange" : "dpBadge--blue"}`}>
                          {r.status === "FECHADO" ? "Fechado" : r.blockers.length ? "Bloqueado" : "Aberto"}
                        </span>
                      </td>
                      <td>
                        <SignatureBadge r={r} />
                      </td>
                      <td>
                        <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => open(r)}>
                          Abrir
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="dpTkCards">
            {(data?.items ?? []).map((r) => (
              <article key={r.id} className="dpTkCard">
                <div className="dpTkCard__head">
                  <span className="dpTkPerson">
                    <strong>{nameOf(r)}</strong>
                    <small>
                      {r.store?.nomeFantasia ?? "Sem loja"}
                      {r.employee.cpf ? ` · ${formatCpf(r.employee.cpf)}` : ""}
                    </small>
                  </span>
                  <span className={`dpBadge ${r.status === "FECHADO" ? "dpBadge--green" : r.blockers.length ? "dpBadge--orange" : "dpBadge--blue"}`}>
                    {r.status === "FECHADO" ? "Fechado" : r.blockers.length ? "Bloqueado" : "Aberto"}
                  </span>
                </div>
                <div className="dpTkCard__grid">
                  <div>
                    <span>Trabalhado</span>
                    {duration(r.workedMinutes)}
                  </div>
                  <div>
                    <span>HE</span>
                    {duration(r.overtimeWorkdayMinutes + r.overtimeRestDayMinutes)}
                  </div>
                  <div>
                    <span>Faltas</span>
                    {r.absenceDays}
                  </div>
                </div>
                {r.signature && r.signature.status !== "NAO_FECHADO" ? (
                  <div>
                    <SignatureBadge r={r} />
                  </div>
                ) : null}
                {issues(r).length ? (
                  <div className="dpTkIssues">
                    {issues(r).map((code) => (
                      <span key={code} className={`dpTkFlag ${r.blockers.includes(code) ? "" : "dpTkFlag--info"}`}>
                        {describeIssue(code)}
                      </span>
                    ))}
                  </div>
                ) : null}
                <div className="dpTkActions">
                  {r.status === "ABERTO" ? (
                    <label className="dpCheckbox" style={{ margin: 0 }}>
                      <input type="checkbox" checked={selected.has(r.contractId)} onChange={() => toggle(r.contractId)} /> Selecionar
                    </label>
                  ) : null}
                  <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => open(r)}>
                    Abrir espelho
                  </button>
                </div>
              </article>
            ))}
            {!data?.items.length && !loading ? <div className="dpEmpty">Nenhum colaborador com ponto neste mês.</div> : null}
          </div>

          {data && data.total > PAGE_SIZE ? <EcoPagination page={page} pageSize={PAGE_SIZE} total={data.total} onChange={setPage} disabled={loading} /> : null}
        </div>
      </main>

      <EcoConfirmDialog
        open={confirm !== null}
        title={confirm === "store" ? `Fechar o ponto de ${storeName ?? "a loja"}?` : `Fechar ${selected.size} espelho(s)?`}
        message={
          <>
            Os espelhos sem pendência ficam fechados para {MONTH_LABELS[month - 1].toLowerCase()} de {year} e passam a valer na folha. Os que tiverem
            pendência continuam abertos e aparecem na lista com o motivo. Reabrir depois exige motivo.
          </>
        }
        confirmLabel="Fechar"
        busy={busy}
        onConfirm={closeMany}
        onCancel={() => setConfirm(null)}
      />

      {result ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">Fechamento do ponto</h2>
              <button className="dpModal__close" onClick={() => setResult(null)}>
                &times;
              </button>
            </header>
            <div className="dpModal__body">
              <p className="dpNotice">{result.closed} espelho(s) fechado(s).</p>
              {result.blocked.length ? (
                <>
                  <p>
                    <strong>{result.blocked.length} continuam abertos:</strong>
                  </p>
                  <ul className="dpRuleList">
                    {result.blocked.map((b) => (
                      <li key={b.contractId}>
                        <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => navigate(DP_TIMESHEET_PAGE(b.contractId, year, month))}>
                          {b.employeeName}
                        </button>{" "}
                        {b.blockers.length ? b.blockers.map(describeIssue).join(" · ") : b.message}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </div>
            <footer className="dpModal__footer">
              <button type="button" className="dpBtn dpBtn--primary" onClick={() => setResult(null)}>
                Ok
              </button>
            </footer>
          </div>
        </div>
      ) : null}
    </div>
  );
}
